import React, { useMemo, useState } from 'react';
import {
  SlSidebar,
  SlSidebarTitle,
  SlSidebarSectionContainer,
  SlSidebarScrollableContainer
} from 'src/components/BeaconV2Components/SlSidebar';
import TopicOwner from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/TopicOwner';
import { useMainEntity } from 'src/utils/Hooks/reduxSelectors';
import { TopicGroupWithOwner } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/types';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { topicSettingsServerProxy, UpdateTopicGroupPayload } from '../serverProxy';
import { useMutation } from '@tanstack/react-query';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';
import GenericSidebarFilter from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/GenericSidebarFilter';
import { ICON_STYLES } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/constants';
import {
  DisabledCheckbox,
  SlDisabledExcludeSelectedCheckbox,
  SlExcludeSelectedCheckbox,
  SlSelectedCheckbox
} from 'src/components/SvgIcons';
import CheckboxItem from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/CheckboxItem';
import { useStacklineTheme } from '@stackline/ui';
import { getTopicOrTopicGroupType } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils';
import SidebarInput from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/SidebarInput';
import { useRefreshTopicEntity } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/hooks/useRefreshTopicEntity';

interface TopicGroupCategorySidebarFilterProps {
  toggleMode: 'include' | 'exclude';
  readOnly: boolean;
  setToggleMode: (mode: 'include' | 'exclude') => void;
  categoryOptions: any[];
  toggledCategories: number[];
  handleSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setToggledCategories: (categories: number[]) => void;
}

const TopicGroupCategorySidebarFilter = (props: TopicGroupCategorySidebarFilterProps) => {
  const [categorySearchInput, setCategorySearchInput] = useState('');
  const theme = useStacklineTheme();

  const getIcon = React.useCallback(() => {
    switch (props?.toggleMode) {
      case 'include': {
        if (props?.readOnly) {
          return <DisabledCheckbox style={ICON_STYLES} />;
        } else {
          return <SlSelectedCheckbox style={ICON_STYLES} />;
        }
      }

      case 'exclude': {
        if (props?.readOnly) {
          return <SlDisabledExcludeSelectedCheckbox style={ICON_STYLES} />;
        } else {
          return <SlExcludeSelectedCheckbox style={ICON_STYLES} />;
        }
      }
      default:
        return null;
    }
  }, [props]);

  const renderCategoryOptions = React.useCallback(() => {
    return (
      <SlSidebarScrollableContainer>
        {props?.categoryOptions
          ?.filter((category) => category?.name?.toLowerCase().includes(categorySearchInput?.toLowerCase()))
          .sort((a, b) => (a.selected === b.selected ? 0 : a.selected ? -1 : 1)) // sort by checked items
          .map((category, index) => (
            <CheckboxItem
              disabled={props?.readOnly}
              onClick={props?.handleSelect}
              value={category.id}
              key={index}
              label={category.name}
              theme={theme}
              checked={category.selected}
              checkedIcon={getIcon()}
            />
          ))}
      </SlSidebarScrollableContainer>
    );
  }, [theme, getIcon, categorySearchInput, props]);

  return (
    <SlSidebarSectionContainer>
      <GenericSidebarFilter
        title="Categories"
        toggleMode={props?.toggleMode}
        handleToggle={props?.setToggleMode}
        includeInput={!props?.readOnly}
        renderItems={renderCategoryOptions}
        includeClear={props?.toggledCategories?.length > 0 && !props?.readOnly}
        includeToggles={!props?.readOnly}
        handleClear={() => props?.setToggledCategories([])}
        sidebarInputProps={{
          placeholder: 'Refine by categories',
          value: categorySearchInput,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => setCategorySearchInput(e.target.value)
        }}
      />
    </SlSidebarSectionContainer>
  );
};

interface TopicGroupSubcategorySidebarFilterProps {
  toggleMode: 'include' | 'exclude';
  readOnly: boolean;
  setToggleMode: (mode: 'include' | 'exclude') => void;
  subcategoryOptions: any[];
  toggledCategories: number[];
  toggledSubcategories: number[];
  handleSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setToggledSubcategories: (subcategories: number[]) => void;
}
const TopicGroupSubcategorySidebarFilter = (props: TopicGroupSubcategorySidebarFilterProps) => {
  const [subcategorySearchInput, setSubcategorySearchInput] = useState('');
  const theme = useStacklineTheme();

  const getIcon = React.useCallback(() => {
    switch (props?.toggleMode) {
      case 'include': {
        if (props?.readOnly) {
          return <DisabledCheckbox style={ICON_STYLES} />;
        } else {
          return <SlSelectedCheckbox style={ICON_STYLES} />;
        }
      }

      case 'exclude': {
        if (props?.readOnly) {
          return <SlDisabledExcludeSelectedCheckbox style={ICON_STYLES} />;
        } else {
          return <SlExcludeSelectedCheckbox style={ICON_STYLES} />;
        }
      }
      default:
        return null;
    }
  }, [props]);

  const renderSubcategoryOptions = React.useCallback(() => {
    return (
      <SlSidebarScrollableContainer>
        {props?.subcategoryOptions
          ?.filter(
            (subcategory) =>
              subcategory.name.toLowerCase().includes(subcategorySearchInput.toLowerCase()) &&
              (props?.toggledCategories.length === 0 ||
                props?.toggledCategories.includes(subcategory.parentCategoryId) ||
                subcategory.selected)
            // And the parent category is selected if any are selected
          )
          .sort((a, b) => (a.selected === b.selected ? 0 : a.selected ? -1 : 1)) // sort by checked items
          .map((subcategory, index) => (
            <CheckboxItem
              disabled={props?.readOnly}
              onClick={props?.handleSelect}
              value={subcategory.id}
              key={index}
              label={subcategory.name}
              theme={theme}
              checked={subcategory.selected}
              checkedIcon={getIcon()}
            />
          ))}
      </SlSidebarScrollableContainer>
    );
  }, [theme, getIcon, subcategorySearchInput, props]);

  return (
    <SlSidebarSectionContainer>
      <GenericSidebarFilter
        title="Subcategories"
        toggleMode={props?.toggleMode}
        handleToggle={props?.setToggleMode}
        includeInput={!props?.readOnly}
        renderItems={renderSubcategoryOptions}
        includeClear={props?.toggledSubcategories?.length > 0 && !props?.readOnly}
        includeToggles={!props?.readOnly}
        handleClear={() => props?.setToggledSubcategories([])}
        sidebarInputProps={{
          placeholder: 'Refine by subcategories',
          value: subcategorySearchInput,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => setSubcategorySearchInput(e.target.value)
        }}
      />
    </SlSidebarSectionContainer>
  );
};

/**
 * Sidebar for updating a custom topic group
 */
export function CustomTopicGroupSettingsSidebar() {
  const mainEntity = useMainEntity() as unknown as TopicGroupWithOwner;
  const [name, setName] = useState(mainEntity.topicGroupName);

  const refreshEntity = useRefreshTopicEntity(mainEntity);

  const topicClassification = getTopicOrTopicGroupType(mainEntity?.sharedWithEntityId, mainEntity?.ownedByUserId);
  const isOwned = topicClassification === 'Owned';
  const readOnly = !isOwned;

  const includedCategories = mainEntity?.includedCategoryIds ?? [];
  const excludedCategories = mainEntity?.excludedCategoryIds ?? [];
  const isIncluding =
    includedCategories.length > 0 || (excludedCategories.length === 0 && includedCategories.length === 0);

  const selectedCategories = isIncluding ? includedCategories : excludedCategories;

  const [toggledCategories, setToggledCategories] = useState<number[]>(selectedCategories ?? []);

  const [categoryToggleMode, setCategoryToggleMode] = useState<'include' | 'exclude'>(
    isIncluding ? 'include' : 'exclude'
  );

  const categories = useAppSelector((state) =>
    state.categories
      .filter((category) => category.id !== 0)
      .map((category) => ({
        ...category,
        selected: toggledCategories.some((selectedCategory) => Number(selectedCategory) === category.id)
      }))
  );

  const categoryOptions = useMemo(() => categories, [categories]);

  const includedSubcategories = mainEntity?.includedSubCategoryIds ?? [];
  const excludedSubcategories = mainEntity?.excludedSubCategoryIds ?? [];
  const isIncludingSubcategories =
    includedSubcategories.length > 0 || (excludedSubcategories.length === 0 && includedSubcategories.length === 0);

  const selectedSubcategories = isIncludingSubcategories ? includedSubcategories : excludedSubcategories;

  const [toggledSubcategories, setToggledSubcategories] = useState<number[]>(selectedSubcategories ?? []);
  const [subcategoryToggleMode, setSubcategoryToggleMode] = useState<'include' | 'exclude'>(
    isIncludingSubcategories ? 'include' : 'exclude'
  );

  // The options to render for the subcategory filter. See renderSubcategoryOptions for details on determining which subcategories to render.
  const subcategories = useAppSelector((state) =>
    state.subCategories.map((subcategory) => ({
      ...subcategory,
      selected: toggledSubcategories.some((selectedSubcategory) => String(selectedSubcategory) === subcategory.id)
    }))
  );
  const subcategoryOptions = useMemo(() => subcategories, [subcategories]);

  const handleCategorySelect = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const id = Number(value);
    setToggledCategories((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  }, []);

  const handleSubcategorySelect = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const id = Number(value);
    setToggledSubcategories((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  }, []);

  const { showSnackbar } = useSnackbar();

  const clearFilters = () => {
    setToggledCategories([]);
    setToggledSubcategories([]);
  };

  const { mutateAsync: handleSave, isLoading } = useMutation({
    mutationFn: async () => {
      const updateTopicGroupPayload: UpdateTopicGroupPayload = {
        topicGroupId: mainEntity.topicGroupId,
        topicGroupName: name
      };

      if (categoryToggleMode === 'include') {
        updateTopicGroupPayload.includedCategoryIds = toggledCategories;
        updateTopicGroupPayload.excludedCategoryIds = [];
      } else {
        updateTopicGroupPayload.includedCategoryIds = [];
        updateTopicGroupPayload.excludedCategoryIds = toggledCategories;
      }

      if (subcategoryToggleMode === 'include') {
        updateTopicGroupPayload.includedSubCategoryIds = toggledSubcategories;
        updateTopicGroupPayload.excludedSubCategoryIds = [];
      } else {
        updateTopicGroupPayload.includedSubCategoryIds = [];
        updateTopicGroupPayload.excludedSubCategoryIds = toggledSubcategories;
      }

      await topicSettingsServerProxy.updateTopicGroup(updateTopicGroupPayload);
    },
    onSuccess: () => {
      refreshEntity();
      showSnackbar({
        type: 'success',
        message: 'Your topic group has been updated successfully!'
      });
    },
    onError: () => {
      showSnackbar({
        type: 'error',
        message: <GenericErrorMessage />
      });
    }
  });

  return (
    <SlSidebar>
      <TopicOwner
        createdAt={mainEntity.createdAt}
        firstName={mainEntity.owner.firstName}
        lastName={mainEntity.owner.lastName}
        userId={mainEntity.ownedByUserId}
      />
      <SlSidebarSectionContainer
        sx={{
          marginTop: '16px'
        }}
      >
        <SlSidebarTitle>Name</SlSidebarTitle>
        <SidebarInput
          value={name}
          onChange={readOnly ? undefined : (e) => setName(e.target.value)}
          placeholder="Name your topic group"
        />
      </SlSidebarSectionContainer>

      <TopicGroupCategorySidebarFilter
        toggleMode={categoryToggleMode}
        readOnly={readOnly}
        setToggleMode={setCategoryToggleMode}
        categoryOptions={categoryOptions}
        toggledCategories={toggledCategories}
        setToggledCategories={setToggledCategories}
        handleSelect={handleCategorySelect}
      />
      <TopicGroupSubcategorySidebarFilter
        setToggledSubcategories={setToggledSubcategories}
        toggleMode={subcategoryToggleMode}
        readOnly={readOnly}
        setToggleMode={setSubcategoryToggleMode}
        subcategoryOptions={subcategoryOptions}
        toggledCategories={toggledCategories}
        toggledSubcategories={toggledSubcategories}
        handleSelect={handleSubcategorySelect}
      />

      {!readOnly && (
        <SlSidebarSectionContainer
          gap="md"
          sx={{
            marginTop: '32px'
          }}
        >
          <SlButton
            disabled={!name.length || (!toggledCategories.length && !toggledSubcategories.length)}
            variant="contained"
            onClick={() => !isLoading && handleSave()}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </SlButton>
          <SlButton onClick={() => clearFilters()}>Clear Filters</SlButton>
        </SlSidebarSectionContainer>
      )}
    </SlSidebar>
  );
}
